import { Controller, useFormContext } from "react-hook-form";
import { MDBRow, MDBCol } from "mdbreact";

import { Label, AbsDropDown } from "abs-ui-components";
import PropTypes from "prop-types";
import "./RiskAssessment.scss";
import { useEffect, useState } from "react";

const High = "High";
const Medium = "Medium";
const Low = "Low";
const Highest = "Highest";
const VeryLow = "Very Low";

// This should match the values in the database
const lowOptions = { label: Low, value: "1" };
const mediumOptions = { label: Medium, value: "2" };
const highOptions = { label: High, value: "3" };

/**
 * Enum for risk options.
 * @readonly
 * @enum {{label: string, value: string}}
 */
const riskOptions = Object.freeze({
	1: lowOptions,
	2: mediumOptions,
	3: highOptions,
	LOW: lowOptions,
	MEDIUM: mediumOptions,
	HIGH: highOptions,
});

const riskOptionsArray = [...new Set(Object.values(riskOptions))];

// used only for detection risk
const detectionRiskOptions = [
	{
		label: Highest,
		value: Highest,
	},
	{
		label: High,
		value: High,
	},
	{
		label: Medium,
		value: Medium,
	},
	{ label: Low, value: Low },
	{
		label: VeryLow,
		value: VeryLow,
	},
];

const defaultRiskOption = riskOptions.MEDIUM;

// the formProperties object is used to set the names of the form fields
const formProperties = {
	inherentRisk: "inherentRisk",
	controlRisk: "controlRisk",
	combinedRisk: "combinedRisk",
};

/**
 * This component is used to display and calculate the risk assessment based on the inherent and control risk.
 * @param { inherentRiskValue } string Inherit Risk value: [1 - 3].
 * @param { controlRiskValue } string Control Risk value: [1 - 3].
 * @param { disabled } bool Disable all the dropdowns.
 */
export const RiskAssessment = ({
	inherentRiskValue = defaultRiskOption.value,
	controlRiskValue = defaultRiskOption.value,
	disabled = false,
}) => {
	const { control, getValues, setValue } = useFormContext();

	useEffect(() => {
		setValue(formProperties.inherentRisk, riskOptions[inherentRiskValue]);
		setValue(formProperties.controlRisk, riskOptions[controlRiskValue]);
		calculateRisk();
	}, [inherentRiskValue, controlRiskValue]);

	// the parameters from this function are used to set/init the default values for the dropdowns
	// if the values are not found in the riskOptions object, the defaultRiskOption is used
	// future updates are done using the controller/getValues from the useFormContext or the setValue function
	const inherentRisk = riskOptions[inherentRiskValue] ?? defaultRiskOption;
	const controlRisk = riskOptions[controlRiskValue] ?? defaultRiskOption;

	const [risks, setRisks] = useState({
		inherentRisk: inherentRisk.label,
		controlRisk: controlRisk.label,
		detectionRisk: Medium,
	});

	const calculateRisk = () => calculateDetectionRisk(getValues, setRisks); // Function to calculate the risk

	// Make sure we update/calculate the risks when form values (getvalues) are pulled correctly
	// getValues() may returns an empty object if the form is not initialized yet
	const hasValues = Object.keys(getValues()).length > 0;
	useEffect(() => {
		calculateRisk();
	}, [hasValues]);

	if (!control || !getValues || !setValue) {
		console.error(
			"Form Provider methods were not defined.",
			"\n",
			"Please check this page for more information: https://react-hook-form.com/docs/useformcontext"
		);
		return null;
	}

	const renderer = ({ field }) =>
		RenderDropdown({ field, calculateRisk, disabled }); // React element to render the dropdown

	// detectionRisk variables are used to update the last column/dropdown
	const detectionRisk = risks.detectionRisk; // drowdown label
	const detectionRiskClass = detectionRisk.replace(/\s+/g, ""); // Remove spaces

	return (
		<section className="risk-assessment">
			<Label
				bold
				name={"Risk Assessment"}
				className="mb-1"
				size="Large"
			/>
			<MDBRow>
				<RenderColumn title="Inherent Risk">
					<Controller
						name={formProperties.inherentRisk}
						control={control}
						defaultValue={inherentRisk}
						render={renderer}
					/>
				</RenderColumn>

				<RenderColumn title="Control Risk">
					<Controller
						name={formProperties.controlRisk}
						control={control}
						defaultValue={controlRisk}
						render={renderer}
					/>
				</RenderColumn>

				<RenderColumn title="Detection Risk">
					<Controller
						name={formProperties.combinedRisk}
						control={control}
						render={() => (
							<article className={`detection-risk ${detectionRiskClass}`}>
								<AbsDropDown
									disabled
									options={detectionRiskOptions}
									selected={detectionRisk}
								/>
							</article>
						)}
					/>
				</RenderColumn>
			</MDBRow>
		</section>
	);
};

RiskAssessment.propTypes = {
	inherentRiskValue: PropTypes.string,
	controlRiskValue: PropTypes.string,
	disabled: PropTypes.bool,
};

/**
 * This component is used to render the columns
 * @param title string
 * @param children node
 */
const RenderColumn = ({ title, children }) => {
	return (
		<MDBCol md="4" className="mt-3 input-div-height">
			<Label bold name={title} className="mb-1" />
			<MDBRow className="d-flex">
				<MDBCol md="12">{children}</MDBCol>
			</MDBRow>
		</MDBCol>
	);
};
RenderColumn.propTypes = {
	title: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
};

/**
 * This component is used to render the dropdown
 * @param field object https://react-hook-form.com/docs/usecontroller/controller
 * @param calculateRisk function to calculate the risk
 * @param disable value of 'true' will disable the dropdown
 */
const RenderDropdown = ({ field, calculateRisk, disabled = false }) => {
	const riskClass = field.value.label;
	return (
		<article className={riskClass}>
			<AbsDropDown
				options={riskOptionsArray}
				disabled={disabled}
				selected={field.value?.value}
				handleChange={(e) => {
					field.onChange(e);
					calculateRisk();
				}}
			/>
		</article>
	);
};
RenderDropdown.propTypes = {
	field: PropTypes.object.isRequired,
	calculateRisk: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

/**
 * This function is used to calculate the detection risk based on the inherent and control risk values
 * @param getValues https://react-hook-form.com/docs/useform/getvalues
 * @param setRisks setter function to update the risk state
 */
const calculateDetectionRisk = (getValues, setRisks) => {
	const { inherentRisk, controlRisk } = getValues();
	const inherentRiskLabel = inherentRisk?.label ?? Medium;
	const controlRiskLabel = controlRisk?.label ?? Medium;

	let riskLabel = Medium;
	if (inherentRiskLabel === Low) {
		switch (controlRiskLabel) {
			case Low:
				riskLabel = Highest;
				break;
			case Medium:
				riskLabel = High;
				break;
			case High:
				riskLabel = Medium;
				break;
		}
	}

	if (inherentRiskLabel === Medium) {
		switch (controlRiskLabel) {
			case Low:
				riskLabel = High;
				break;
			case Medium:
				riskLabel = Medium;
				break;
			case High:
				riskLabel = Low;
				break;
		}
	}

	if (inherentRiskLabel === High) {
		switch (controlRiskLabel) {
			case Low:
				riskLabel = Medium;
				break;
			case Medium:
				riskLabel = Low;
				break;
			case High:
				riskLabel = VeryLow;
				break;
		}
	}

	setRisks({
		inherentRisk: inherentRiskLabel,
		controlRisk: controlRiskLabel,
		detectionRisk: riskLabel,
	});
};
